import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  createAssessmentSession,
  deleteAssessmentSession,
  type SessionCredentials,
} from "../../services/api";

const STORAGE_ID = "a2p_assessment_session_id";
const STORAGE_TOKEN = "a2p_assessment_access_token";
interface SessionValue {
  credentials: SessionCredentials | null;
  hasResumableSession: boolean;
  busy: boolean;
  startNew(): Promise<SessionCredentials>;
  clearLocal(): void;
  deleteCurrent(): Promise<void>;
}
const SessionContext = createContext<SessionValue | null>(null);

function readStored(): SessionCredentials | null {
  const sessionId = localStorage.getItem(STORAGE_ID);
  const accessToken = localStorage.getItem(STORAGE_TOKEN);
  return sessionId && accessToken ? { sessionId, accessToken } : null;
}
function store(credentials: SessionCredentials) {
  localStorage.setItem(STORAGE_ID, credentials.sessionId);
  localStorage.setItem(STORAGE_TOKEN, credentials.accessToken);
}

export function AssessmentSessionProvider({ children }: PropsWithChildren) {
  const [credentials, setCredentials] = useState<SessionCredentials | null>(
    () => readStored(),
  );
  const [busy, setBusy] = useState(false);
  const clearLocal = useCallback(() => {
    localStorage.removeItem(STORAGE_ID);
    localStorage.removeItem(STORAGE_TOKEN);
    setCredentials(null);
  }, []);
  const startNew = useCallback(async () => {
    setBusy(true);
    try {
      const created = await createAssessmentSession();
      store(created);
      setCredentials(created);
      return created;
    } finally {
      setBusy(false);
    }
  }, []);
  const deleteCurrent = useCallback(async () => {
    if (!credentials) return;
    setBusy(true);
    try {
      await deleteAssessmentSession(credentials);
      clearLocal();
    } finally {
      setBusy(false);
    }
  }, [clearLocal, credentials]);
  const value = useMemo(
    () => ({
      credentials,
      hasResumableSession: Boolean(credentials),
      busy,
      startNew,
      clearLocal,
      deleteCurrent,
    }),
    [busy, clearLocal, credentials, deleteCurrent, startNew],
  );
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
export function useAssessmentSession() {
  const value = useContext(SessionContext);
  if (!value)
    throw new Error(
      "useAssessmentSession must be used inside AssessmentSessionProvider",
    );
  return value;
}
